import { IntroSection } from "./IntroSection";
import { hiringEventsViewJobDetailsDataAdapter } from './utils/hiringEventsViewJobDetailsDataAdapter';
import { Box, Divider } from '../../../../../../../mads';
import { HtmlContent } from '../../../../../../../shared/components/HtmlContent';
import { useI18n } from '../../../../../../../shared/I18n';
import { useViewjobProctorAllocation } from '../../../../../../proctor/__generated__/ViewjobProctorContext';
import { HeRnViewjobOverviewTst } from '../../../../../../proctor/__generated__/ViewjobProctorSpecifications';
import {
    useHiringEventsData,
    useViewjobOneGraphModel,
} from '../../../../../context/ViewJobDataContext';
import ViewJobLabelledContent from '../common/ViewJobLabelledContent/ViewJobLabelledContent';
import { ViewJobList } from '../common/ViewJobList/ViewJobList';

import { AvroOptional } from '@indeed/signals-logger';
import { log as logRNHiringEventDetailsShown } from '@indeed/signals-schema/com/indeed/signals/schema/hiringevents/RNHiringEventDetailsShown';
import React, { useEffect } from 'react';
import { StyleSheet } from 'react-native';

export type HiringEventsViewJobDetailsProps = {
    requirements: string[];
    technologyRequirements: string;
    requiredItems: string[];
    useVip: boolean;
    directionsHTML: string;
    dressCodeDescription: string;
    companyName: string;
    companyAboutHTML: string;
    perks: string[];
    isRemote: boolean;
    introHeader: string;
    introContent: string;
    hiringEventUuid: string;
    jobKey: string;
};
export const HiringEventsViewJobDetails: React.FunctionComponent<
    HiringEventsViewJobDetailsProps
> = ({
    companyAboutHTML,
    companyName,
    directionsHTML,
    dressCodeDescription,
    hiringEventUuid,
    introContent,
    introHeader,
    isRemote,
    jobKey,
    perks,
    requiredItems,
    requirements,
    technologyRequirements,
    useVip,
}) => {
    const gt = useI18n();

    useEffect(() => {
        logRNHiringEventDetailsShown({
            jobkey: AvroOptional.string(jobKey),
            hiringEventUuid: AvroOptional.string(hiringEventUuid),
        });
    }, [jobKey, hiringEventUuid]);

    return (
        <>
            <Box
                id="HiringEventsViewJobDetailsSection"
                sx={styles.hiringEventsViewJobDetailsSectionContainer}
            >
                {directionsHTML.length !== 0 && (
                    <ViewJobLabelledContent
                        label={gt.pgettext(
                            'View job details - how to attend section',
                            'How to attend',
                        )}
                    >
                        <HtmlContent>{directionsHTML}</HtmlContent>
                    </ViewJobLabelledContent>
                )}
                {requirements.length !== 0 && (
                    <ViewJobLabelledContent
                        label={gt.pgettext(
                            'View job details - job requirements section',
                            'Job requirements',
                        )}
                    >
                        <ViewJobList
                            items={requirements.map((requirement) => ({
                                key: requirement,
                                label: requirement,
                            }))}
                        />
                    </ViewJobLabelledContent>
                )}
                {technologyRequirements.length !== 0 && (
                    <ViewJobLabelledContent
                        label={gt.pgettext(
                            'View job details - technical requirements section',
                            'Technical requirements',
                        )}
                    >
                        <HtmlContent>{technologyRequirements}</HtmlContent>
                    </ViewJobLabelledContent>
                )}
                {requiredItems.length !== 0 && (
                    <ViewJobLabelledContent
                        label={gt.pgettext(
                            'View job details - what to prepare section',
                            'What to prepare',
                        )}
                    >
                        <ViewJobList
                            items={requiredItems.map((requiredItem) => ({
                                key: requiredItem,
                                label: requiredItem,
                            }))}
                        />
                    </ViewJobLabelledContent>
                )}
                {dressCodeDescription.length !== 0 && !isRemote && (
                    <ViewJobLabelledContent
                        label={gt.pgettext(
                            'View job details - dress code section',
                            'Preferred dress code',
                        )}
                    >
                        <HtmlContent>{dressCodeDescription}</HtmlContent>
                    </ViewJobLabelledContent>
                )}
                {companyAboutHTML.length !== 0 && (
                    <ViewJobLabelledContent
                        label={gt.pgettext(
                            'View job details - about company section',
                            'About {0}',
                            companyName,
                        )}
                    >
                        <HtmlContent>{companyAboutHTML}</HtmlContent>
                    </ViewJobLabelledContent>
                )}
                {perks.length !== 0 && (
                    <ViewJobLabelledContent
                        label={gt.pgettext(
                            'View job details - perks and benefits section',
                            'Perks and benefits',
                        )}
                    >
                        <ViewJobList
                            items={perks.map((perk) => ({
                                key: perk,
                                label: perk,
                            }))}
                        />
                    </ViewJobLabelledContent>
                )}
                <IntroSection isRemote={isRemote} useVip={useVip} introHeader={introHeader} introContent={introContent} gt={gt} />
            </Box>
            <Divider />
        </>
    );
};

export const HiringEventsViewJobDetailsWithContext: React.FC = () => {
    const gt = useI18n();
    const viewjobOneGraphModel = useViewjobOneGraphModel();
    const proctorAllocation = useViewjobProctorAllocation(HeRnViewjobOverviewTst);
    const hiringEventData = useHiringEventsData();

    if (!proctorAllocation.isTest || !hiringEventData || !viewjobOneGraphModel?.job.jobKey) {
        return null;
    }

    const hiringEventsViewJobDetailsProps = hiringEventsViewJobDetailsDataAdapter(
        gt,
        hiringEventData,
    );

    return (
        <HiringEventsViewJobDetails
            jobKey={viewjobOneGraphModel.job.jobKey}
            {...hiringEventsViewJobDetailsProps}
        />
    );
};

const styles = StyleSheet.create({
    hiringEventsViewJobDetailsSectionContainer: {
        marginVertical: 24,
        flex: 1,
    },
    estimatedDurationHelperText: {
        fontStyle: 'italic',
    },
});
